import { Settings } from 'lucide-react';
import styles from './JiraProjectsNextStep.module.css';

/**
 * What to do once Jira is connected: map each project (Jason, 15 Oct, local
 * ticket t-HUlenl). A project sends to Jira only once it is mapped, on its own
 * settings page, and the way there is the gear a project shows when the
 * sidebar row is hovered, or the one on its board.
 */
export function JiraProjectsNextStep() {
  return (
    <section className={styles.step} aria-labelledby="jira-next-step">
      <div className={styles.text}>
        <h2 id="jira-next-step">Next, map each project</h2>
        <p>
          Each Toado project sends to one Jira project. Map it on the project&apos;s settings page, under <strong>Jira export</strong>.
        </p>
        <ol>
          <li>Hover the project in the left sidebar and click its <Settings size={13} className={styles.inlineIcon} aria-label="gear" /> gear, or click the <Settings size={13} className={styles.inlineIcon} aria-label="gear" /> gear at the top of its board.</li>
          <li>Open <strong>Jira export</strong> in the project&apos;s settings, and choose the Jira project and issue types.</li>
        </ol>
      </div>
      <MapProjectArt />
    </section>
  );
}

/**
 * The way to a project's Jira export, drawn from the app as it is: the sidebar
 * row whose count gives way to the gear on hover, then the project settings
 * with Jira export picked. The text beside it says the same, so it is hidden
 * from screen readers; at rest, and for anyone who asks for less motion, it
 * shows the last step.
 */
export function MapProjectArt() {
  return (
    <svg className={styles.art} viewBox="0 0 220 96" aria-hidden="true" focusable="false" data-testid="jira-map-project-art">
      {/* The sidebar, three projects. */}
      <rect className={styles.panel} x={1.5} y={1.5} width={92} height={93} rx={7} />
      <rect className={styles.faint} x={9} y={10} width={30} height={3} rx={1.5} />
      {[0, 1, 2].map((row) => (
        <g key={row}>
          {row === 1 && <rect className={styles.hoverRow} x={5} y={19 + row * 18} width={85} height={15} rx={4} />}
          <circle className={row === 1 ? styles.dotAccent : styles.dot} cx={14} cy={26.5 + row * 18} r={2.6} />
          <rect className={styles.ink} x={21} y={25 + row * 18} width={row === 1 ? 38 : 32 - row * 4} height={3} rx={1.5} />
          {row !== 1 && <rect className={styles.faint} x={79} y={25 + row * 18} width={6} height={3} rx={1.5} />}
        </g>
      ))}
      {/* Row two's count gives way to the gear on hover. */}
      <rect className={`${styles.faint} ${styles.count}`} x={79} y={43} width={6} height={3} rx={1.5} />
      <g className={styles.gear}>
        <Settings x={76.5} y={39.5} width={10} height={10} strokeWidth={2.4} />
      </g>
      <path className={`${styles.cursor} ${styles.pointer}`} d="M80 47 L80 57 L82.6 54.6 L84.5 58.6 L86.2 57.8 L84.4 53.9 L87.8 53.7 Z" />

      {/* On to the project's settings. */}
      <path className={styles.arrow} d="M101 48 H118 M114 44 L118 48 L114 52" />

      {/* The project settings: its sections, Jira export picked. */}
      <rect className={styles.panel} x={125.5} y={1.5} width={93} height={93} rx={7} />
      <rect className={styles.faint} x={133} y={10} width={34} height={3} rx={1.5} />
      {[0, 1, 2, 3].map((row) => (
        <rect key={row} className={styles.ink} x={133} y={22 + row * 12} width={[30, 26, 36, 24][row]} height={3} rx={1.5} />
      ))}
      <g className={styles.picked}>
        <rect className={styles.pickedRow} x={129} y={67} width={84} height={14} rx={4} />
        <text className={styles.pickedText} x={135} y={76.5}>Jira export</text>
      </g>
    </svg>
  );
}
